import { Link } from 'react-router-dom';
import { formatSeasonStart } from '../../utils/season';
import {
  NEW_TESTAMENT_CHAPTERS,
  OLD_TESTAMENT_CHAPTERS,
  pickMilestone
} from '../../utils/progressStats';
import './ProgressSummary.css';

const percentOf = (value, total) => (total ? Math.round((value / total) * 100) : 0);

/**
 * The headline of My Progress: how much of the Bible is read, a few chips,
 * and the Old/New Testament split. Everything secondary lives in MoreStats.
 *
 * `profile` belongs to the first selected reader, so streak and Bible
 * completions only show when one reader is selected.
 */
const ProgressSummary = ({
  stats,
  derived,
  profile,
  singleReader,
  yearScope,
  seasonStart,
  competitiveMode = false,
  publicProfileReaderId = null
}) => {
  const {
    totalChapters,
    uniqueChaptersRead,
    percentComplete,
    oldTestamentRead,
    newTestamentRead,
    bulkChapters = 0
  } = stats;
  const profileStats = profile?.stats;
  const profileLoading = singleReader && !profile;
  const streak = profileStats?.currentStreak ?? 0;
  const biblesCompleted = profileStats?.completedBibles ?? 0;
  const milestone = pickMilestone(stats);

  const scopeNote = yearScope === 'season'
    ? (seasonStart ? `since ${formatSeasonStart(seasonStart)}` : 'this year')
    : 'all years';
  const bulkOnly = uniqueChaptersRead === 0 && bulkChapters > 0;

  const chips = [];
  if (singleReader) {
    chips.push({
      key: 'streak',
      value: profileLoading ? null : streak,
      label: streak === 1 ? 'day' : 'days',
      icon: '🔥'
    });
  }
  chips.push({
    key: 'chapters',
    value: (derived.chaptersRead ?? 0).toLocaleString(),
    label: 'chapters'
  });
  chips.push({
    key: 'books',
    value: `${derived.booksFinished ?? 0}/${derived.booksTotal ?? 66}`,
    label: 'books'
  });
  if (singleReader && biblesCompleted >= 1) {
    chips.push({
      key: 'bibles',
      value: biblesCompleted,
      label: biblesCompleted === 1 ? 'Bible' : 'Bibles'
    });
  }

  const testaments = [
    { key: 'old', label: 'OT', read: oldTestamentRead, total: OLD_TESTAMENT_CHAPTERS },
    { key: 'new', label: 'NT', read: newTestamentRead, total: NEW_TESTAMENT_CHAPTERS }
  ];

  return (
    <section className="progress-summary" aria-label="Reading progress summary">
      <div className="progress-summary-hero">
        <p className="progress-summary-percent">
          <strong>{percentComplete}%</strong> of the Bible
        </p>
        <div
          className="progress-summary-bar"
          role="progressbar"
          aria-valuenow={percentComplete}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label="Percent of the Bible read"
        >
          <span style={{ width: `${percentComplete}%` }} />
        </div>
        <p className="progress-summary-caption">
          {uniqueChaptersRead.toLocaleString()} of {totalChapters.toLocaleString()} chapters · {scopeNote}
        </p>
        {bulkOnly && (
          <p className="progress-summary-caption">
            Your {bulkChapters.toLocaleString()} chapters were logged as bulk totals, which
            don't name chapters, so they can't fill in the map.
          </p>
        )}
      </div>

      <ul className="progress-summary-chips">
        {chips.map((chip) => (
          <li key={chip.key} className={`progress-chip progress-chip--${chip.key}`}>
            <span className="progress-chip-value">
              {chip.icon && <span aria-hidden="true">{chip.icon} </span>}
              {chip.value == null ? <span className="progress-chip-skeleton" aria-label="Loading" /> : chip.value}
            </span>
            <span className="progress-chip-label">{chip.label}</span>
          </li>
        ))}
      </ul>

      <div className="progress-summary-testaments">
        {testaments.map((t) => {
          const pct = percentOf(t.read, t.total);
          return (
            <div key={t.key} className={`testament-bar testament-bar--${t.key}`}>
              <span className="testament-bar-label">{t.label}</span>
              <div
                className="testament-bar-track"
                role="progressbar"
                aria-valuenow={pct}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-label={`${t.key === 'old' ? 'Old' : 'New'} Testament`}
              >
                <span style={{ width: `${pct}%` }} />
              </div>
              <span className="testament-bar-value">{pct}%</span>
            </div>
          );
        })}
      </div>

      {(milestone || competitiveMode) && (
        <div className="progress-summary-footer">
          {milestone && <p className="progress-summary-milestone">🌟 {milestone.text}</p>}
          {competitiveMode && publicProfileReaderId && (
            <Link to={`/u/${publicProfileReaderId}`} className="progress-summary-link">Public profile →</Link>
          )}
          {competitiveMode && <Link to="/leaderboard" className="progress-summary-link">Leaderboard →</Link>}
        </div>
      )}
    </section>
  );
};

export default ProgressSummary;
